import { useState, useEffect } from "react";
import { useStore } from "../../contexts/StoreContext";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchForms } from "../../api/queries";
import { createFlyer } from "../../api/mutations";
import { QRCodeSVG } from "qrcode.react";
import { useNavigate } from "react-router-dom";
import jsPDF from "jspdf";
import html2canvas from "html2canvas-pro";
import { useLanguage } from "../../contexts/LanguageContext";
import { RichTextEditor } from "../../components/RichTextEditor";
import TrialEndedAlert from "../../components/TrialEndedAlert";
import Header from "../../components/Header";
import Button from "../../components/Button";
import { PAPER_SIZES, FONT_SCALE } from "../../constants/flyer";

export default function NewFlyer() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { selectedStore } = useStore();
  const { t } = useLanguage();
  const [isTrialEndedAlertOpen, setIsTrialEndedAlertOpen] = useState(false);

  // Fetch available forms
  const { data: forms } = useQuery({
    queryKey: ["forms", selectedStore?.id],
    queryFn: () => fetchForms(selectedStore?.id),
    enabled: !!selectedStore?.id,
  });

  const [formData, setFormData] = useState({
    paperSize: "a4",
    selectedFormId: "",
    selectedForm: null,
    headerText: t("flyers.new.form.defaultHeaderText"),
    backgroundColor: selectedStore?.background_color,
    textColor: selectedStore?.primary_color,
    description: `<p style="text-align: center">${t("flyers.preview.scanInfo.title")}<br />
${t("flyers.preview.scanInfo.description")}<br />
<strong>${t("flyers.preview.scanInfo.apple")}</strong> & <strong>${t("flyers.preview.scanInfo.google")}</strong></p>`,
  });

  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (forms?.length > 0) {
      setFormData((prev) => ({
        ...prev,
        selectedFormId: forms[0].id,
        selectedForm: forms[0],
      }));
    }
  }, [forms]);

  const createFlyerMutation = useMutation({
    mutationFn: createFlyer,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["flyers", selectedStore?.id],
      });
      // Same reason as the form step: the wizard reads has_flyers off
      // "setup-status". This screen stays put inside /setup rather than
      // navigating, so without this the step never ticks over at all.
      queryClient.invalidateQueries({
        queryKey: ["setup-status", selectedStore?.id],
      });

      if (!window.location.pathname.includes("/setup")) {
        navigate("/flyers");
      }
    },
  });

  const handleSave = async () => {
    if (!formData.selectedForm) {
      alert(t("flyers.new.validation.selectForm"));
      return;
    }

    setIsSaving(true);
    try {
      // Check if store is in trial period (15 days from creation)
      const isInTrialPeriod = () => {
        if (!selectedStore?.trial_expires_at) return false;
        const trialEndDate = new Date(selectedStore.trial_expires_at);
        trialEndDate.setDate(trialEndDate.getDate());
        return new Date() < trialEndDate;
      };

      // Check subscription limits
      if (
        selectedStore?.subscription_status !== "active" &&
        selectedStore?.subscription_status !== "past_due" &&
        selectedStore?.subscription_status !== "trialing" &&
        !isInTrialPeriod() &&
        selectedStore?.setup_completed
      ) {
        setIsTrialEndedAlertOpen(true);
        return;
      }

      // Generate PDF blob
      const pdfBlob = await generatePDFBlob();

      const flyerData = {
        form_id: formData.selectedFormId,
        paper_size: formData.paperSize,
        header_text: formData.headerText,
        background_color: formData.backgroundColor,
        text_color: formData.textColor,
        description: formData.description,
      };

      // Create FormData
      const formDataToSend = new FormData();
      formDataToSend.append("flyer", JSON.stringify(flyerData));
      formDataToSend.append("pdf", pdfBlob, "flyer.pdf");

      await createFlyerMutation.mutateAsync({
        storeId: selectedStore.id,
        formData: formDataToSend,
      });
    } catch (error) {
      console.error("Error saving flyer:", error);
    } finally {
      setIsSaving(false);
    }
  };

  // Helper function to generate PDF blob
  const generatePDFBlob = async () => {
    const element = document.querySelector(".preview-content").cloneNode(true);
    const images = element.getElementsByTagName("img");

    // Convert S3 images to data URLs first
    await Promise.all(
      Array.from(images).map(async (img) => {
        if (/loyaltify-(production|staging)\.s3/.test(img.src)) {
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");

          return new Promise((resolve, reject) => {
            const newImg = new Image();
            newImg.crossOrigin = "anonymous";
            newImg.onload = () => {
              canvas.width = newImg.naturalWidth;
              canvas.height = newImg.naturalHeight;
              ctx.drawImage(newImg, 0, 0);
              canvas.toBlob(
                (blob) => {
                  if (!blob) {
                    reject(new Error("Canvas is empty"));
                    return;
                  }
                  img.src = URL.createObjectURL(blob);
                  resolve();
                },
                "image/png",
                1,
              );
            };
            newImg.onerror = reject;
            // Add timestamp to bypass cache
            newImg.src = `${img.src}?t=${Date.now()}`;
          });
        }
      }),
    );

    const pxWidth = PAPER_SIZES[formData.paperSize].width * 37.8;
    const pxHeight = PAPER_SIZES[formData.paperSize].height * 37.8;

    // Size the base font as a fraction of the paper WIDTH (the same FONT_SCALE
    // the preview uses via cqw), so the download matches the on-screen preview
    // and text scales proportionally with the chosen paper size. A fixed px here
    // would make text look huge on small paper (A6) and tiny on large (Letter).
    element.style.fontSize = `${pxWidth * FONT_SCALE}px`;

    const wrapper = document.createElement("div");
    wrapper.style.cssText = `
            width: ${pxWidth}px;
            height: ${pxHeight}px;
            position: relative;
            background-color: ${formData.backgroundColor};
            overflow: hidden;
        `;
    wrapper.appendChild(element);
    document.body.appendChild(wrapper);

    try {
      const canvas = await html2canvas(wrapper, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        imageTimeout: 0,
        logging: true,
        backgroundColor: null,
      });

      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "cm",
        format: [
          PAPER_SIZES[formData.paperSize].width,
          PAPER_SIZES[formData.paperSize].height,
        ],
      });

      const imgData = canvas.toDataURL("image/jpeg", 1.0);
      pdf.addImage(
        imgData,
        "JPEG",
        0,
        0,
        PAPER_SIZES[formData.paperSize].width,
        PAPER_SIZES[formData.paperSize].height,
      );

      return pdf.output("blob");
    } finally {
      document.body.removeChild(wrapper);
    }
  };

  return (
    <>
      <div className="max-w-8xl mx-auto space-y-6 lg:space-y-8">
        {/* Header */}
        <Header
          title={t("flyers.new.title")}
          description={t("flyers.new.description")}
          rightSection={
            <Button onClick={handleSave} isSaving={isSaving}>
              {t("flyers.new.button")}
            </Button>
          }
        />
        {/* Main Content */}
        <div className="grid lg:grid-cols-2 gap-6 lg:gap-8">
          {/* Form Section */}
          <div className="w-full">
            <div className="bg-white rounded-lg shadow-sm p-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t("flyers.new.form.selectForm")}
                </label>
                <select
                  value={formData.selectedFormId}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      selectedFormId: e.target.value,
                      selectedForm: forms?.find((f) => f.id == e.target.value),
                    }))
                  }
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-xs focus:border-indigo-500 focus:ring-indigo-500"
                >
                  {forms?.map((form) => (
                    <option key={form.id} value={form.id}>
                      {form.pass.name} - Form #{form.id}
                    </option>
                  ))}
                </select>
              </div>

              <div className="pt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t("flyers.new.form.headerText")}
                </label>
                <input
                  value={formData.headerText}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      headerText: e.target.value,
                    }))
                  }
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-xs focus:border-indigo-500 focus:ring-indigo-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-4 pt-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    {t("flyers.new.form.backgroundColor")}
                  </label>
                  <input
                    type="color"
                    value={formData.backgroundColor}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        backgroundColor: e.target.value,
                      }))
                    }
                    className="mt-1 block w-full h-9 rounded-md border-gray-300"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    {t("flyers.new.form.textColor")}
                  </label>
                  <input
                    type="color"
                    value={formData.textColor}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        textColor: e.target.value,
                      }))
                    }
                    className="mt-1 block w-full h-9 rounded-md border-gray-300"
                  />
                </div>
              </div>

              <div className="pt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t("flyers.new.form.description")}
                </label>
                <RichTextEditor
                  content={formData.description}
                  onChange={(html) =>
                    setFormData((prev) => ({ ...prev, description: html }))
                  }
                />
              </div>

              {/* Paper Size Selection */}
              <div className="mb-2 mt-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t("flyers.new.form.paperSize")}
                </label>
                <div className="grid grid-cols-2 gap-4">
                  {Object.entries(PAPER_SIZES).map(([key, size]) => (
                    <div
                      key={key}
                      onClick={() =>
                        setFormData((prev) => ({ ...prev, paperSize: key }))
                      }
                      className={`
                                                    border rounded-lg p-4 cursor-pointer
                                                    ${
                                                      formData.paperSize === key
                                                        ? "border-indigo-500 bg-indigo-50"
                                                        : "border-gray-200 hover:border-gray-300"
                                                    }
                                            `}
                    >
                      <h3 className="font-medium text-gray-900">{size.name}</h3>
                      <p className="text-sm text-gray-500">
                        {size.description}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex justify-end">
                <button
                  type="submit"
                  onClick={handleSave}
                  disabled={isSaving}
                  className={`inline-flex items-center px-6 py-2 border border-transparent text-sm font-medium rounded-lg text-white ${
                    isSaving
                      ? "bg-indigo-400 cursor-not-allowed"
                      : "bg-indigo-600 hover:bg-indigo-700"
                  } focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors`}
                >
                  <div className="flex items-center">
                    {isSaving && (
                      <div className="mr-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-b-transparent border-white"></div>
                      </div>
                    )}
                    {isSaving ? t("common.saving") : t("flyers.new.button")}
                  </div>
                </button>
              </div>
            </div>
          </div>

          {/* Preview Section */}
          {/* translate="no": this preview is rasterized into the exported
              flyer PDF (html2canvas → jsPDF), so Google Translate rewriting
              text here would bake machine-translated copy into the download
              (see domPatches.js). */}
          <div className="w-full" translate="no">
            <div className="sticky top-8">
              <div className="bg-white rounded-lg shadow-sm p-4">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-lg font-medium text-gray-900">
                    {t("flyers.new.preview.title")}
                  </h2>
                </div>
                <div
                  className="relative w-full"
                  style={{
                    paddingBottom: `${(PAPER_SIZES[formData.paperSize].height / PAPER_SIZES[formData.paperSize].width) * 100}%`,
                    // Make this box a query container so the flyer's font-size
                    // can be a % of its width (cqw) rather than the viewport.
                    containerType: "inline-size",
                  }}
                >
                  <div
                    className={`absolute inset-0 border border-gray-200 rounded-lg preview-content overflow-hidden flex flex-col items-center pt-[5%] pb-[7%] ${formData.selectedForm ? "" : "hidden"}`}
                    style={{
                      backgroundColor: formData.backgroundColor,
                      // % of the flyer's own width — matches the PDF's FONT_SCALE.
                      fontSize: `${FONT_SCALE * 100}cqw`,
                    }}
                  >
                    {/* One flow column, top to bottom. Nothing is absolutely
                        positioned, so no two blocks can print on top of each
                        other whatever the logo, the header length, the rich-text
                        description or the paper size.

                        Earlier layouts pinned the QR stack from the top and the
                        text blocks from the bottom. A `top`/`bottom` percentage
                        resolves against the page HEIGHT while every other measure
                        here is a % of its WIDTH, so the gap between the two halves
                        moved with the paper's aspect ratio: Letter (22x28, ratio
                        1.27) is much squarer than A4/A5/A6 (~1.41), and printed
                        the instructions over the QR code. In a flow column the
                        question cannot arise — spacing is a % of the width
                        throughout, which is also what keeps the PDF identical to
                        the preview at every paper size. */}
                    {formData.selectedForm?.store.logo_url && (
                      <img
                        src={formData.selectedForm?.store.logo_url}
                        alt="Store Logo"
                        className="w-[40%] h-auto object-contain shrink-0"
                      />
                    )}

                    {/* The gaps are elements, not margins, so they can give way.
                        A flex item shrinks before it overflows, but a margin never
                        does — with fixed margins a two-line header plus a long
                        description ran off the bottom of a Letter page while the
                        white space above it sat untouched. As spacers they now
                        close up first, in proportion to each other, and only what
                        is left over pushes at the page edge. Sized in em, which
                        tracks the width on both the preview and the export path;
                        2.6em is the 8% of the width this gap used to be. */}
                    <div className="shrink grow-0 basis-[2.6em] min-h-0" />

                    {/* Header Text */}
                    <div
                      className="w-full shrink-0 text-center text-[1.5em] font-bold"
                      style={{
                        color: formData.textColor,
                        lineHeight: "1.25em",
                      }}
                    >
                      {formData.headerText}
                    </div>

                    <div className="shrink grow-0 basis-[1.3em] min-h-0" />

                    {/* QR Code. shrink-0 matters here beyond spacing: a flex item
                        squashed along the main axis would render the code as a
                        rectangle, and a distorted QR does not scan. */}
                    <div className="w-[40%] shrink-0">
                      <QRCodeSVG
                        style={{ width: "100%", height: "100%" }}
                        value={`${window.location.origin}/f/${formData.selectedForm?.uuid}`}
                        level="H"
                        margin={2}
                        includeMargin={true}
                      />
                    </div>

                    <div className="shrink grow-0 basis-[2.25em] min-h-0" />

                    {/* Instructions Text. Unitless line-height so it inherits as
                        a multiplier — an H1/H2 in the rich text gets a line box
                        scaled to its own font size, not the base, else big lines
                        overlap. */}
                    <div
                      className="w-full shrink-0"
                      style={{ color: formData.textColor, lineHeight: 1.3 }}
                    >
                      <div
                        className="text-[0.875em]"
                        dangerouslySetInnerHTML={{
                          __html: formData.description,
                        }}
                      />
                    </div>

                    {/* All the leftover height collects here, below the content
                        and above the footer. Putting it between the QR and the
                        instructions instead left a hole in the middle of the page
                        — worst on the advanced plan, where there is no footer to
                        take up any of it. It is also the first thing to give: a
                        long description or a wrapped header eats this before
                        anything moves toward the page edge. */}
                    <div className="flex-1 min-h-0" />

                    {/* Footer. White-labelled away on the advanced plan — the
                        PDF export clones .preview-content, so dropping it here
                        drops it from the exported flyer too. */}
                    {selectedStore?.current_plan !== "advanced" && (
                      <div
                        className="w-full shrink-0 text-center text-[0.75em]"
                        style={{ color: formData.textColor }}
                      >
                        Powered by Loyaltify.io
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <TrialEndedAlert
        isOpen={isTrialEndedAlertOpen}
        onClose={() => setIsTrialEndedAlertOpen(false)}
        subscriptionStatus={selectedStore?.subscription_status}
      />
    </>
  );
}
